import { createAsyncThunk } from '@reduxjs/toolkit';

import { documentosPadraoService } from './services';
import type {
  DocumentoPadrao,
  DocumentoPadraoFormInput,
  DocumentoPadraoParte,
  DocumentoPadraoUso,
  DocumentosPadraoConsulta,
  DocumentosPadraoContexto,
  DocumentosPadraoDados,
  ErroMutacaoDocumentoPadrao,
} from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { ErroHttp } from '@/shared/utils/erroHttp';
import type { RootState } from '@/store';

const mensagemErro = (erro: unknown, fallback: string): string =>
  erro instanceof Error && erro.message ? erro.message : fallback;

const paraErroMutacao = (erro: unknown, fallback: string): ErroMutacaoDocumentoPadrao => ({
  mensagem: mensagemErro(erro, fallback),
  status: erro instanceof ErroHttp ? erro.status : null,
  codigo: erro instanceof ErroHttp ? erro.codigo : null,
});

const STATUS_DE_LISTA_VELHA = [404, 409];

/** A consulta corrente sai do próprio state: toda mutação recarrega a MESMA página. */
const consultaAtual = (state: RootState): DocumentosPadraoConsulta => ({
  aba: state.documentosPadrao.aba,
  filtros: state.documentosPadrao.filtros,
  page: state.documentosPadrao.paginacao.page,
  limit: state.documentosPadrao.paginacao.limit,
});

/**
 * A consulta NUNCA vem por parâmetro: aba, filtros e página já moram no state, e quem os muda são
 * os reducers. Com o parâmetro, `limit` tinha duas fontes (a constante do hook e o `paginacao.limit`
 * que o servidor devolve) — exatamente o par que diverge em silêncio.
 */
export const carregarDocumentosPadrao = createAsyncThunk<
  DocumentosPadraoDados,
  void,
  { rejectValue: string; state: RootState }
>('documentosPadrao/carregar', async (_arg, thunkAPI) => {
  try {
    return await documentosPadraoService.carregar(consultaAtual(thunkAPI.getState()));
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      mensagemErro(erro, 'Não foi possível carregar os documentos padrão.'),
    );
  }
});

export const buscarDocumentosPadraoParaArbitragem = createAsyncThunk<
  DocumentosPadraoDados,
  { busca: string; page: number },
  { rejectValue: { mensagem: string; status: number | null } }
>('documentosPadrao/buscarParaArbitragem', async ({ busca, page }, thunkAPI) => {
  try {
    return await documentosPadraoService.carregarParaArbitragem({
      aba: 'todos',
      filtros: {
        busca,
        tipo: 'todos',
        setorId: 'todos',
        produto: 'ARBITRAGEM',
        validade: 'todas',
      },
      page,
      limit: 20,
    });
  } catch (erro) {
    return thunkAPI.rejectWithValue({
      mensagem: mensagemErro(erro, 'Não foi possível buscar os documentos padrão.'),
      status: erro instanceof ErroHttp ? erro.status : null,
    });
  }
});

export const preVisualizarDocumentoPadraoParaArbitragem = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('documentosPadrao/preVisualizarParaArbitragem', async (id, thunkAPI) => {
  try {
    await documentosPadraoService.preVisualizar(id);
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      mensagemErro(erro, 'Não foi possível abrir a pré-visualização.'),
    );
  }
});

export const verificarDocumentoPadraoParaArbitragem = createAsyncThunk<
  DocumentoPadrao,
  string,
  { rejectValue: { mensagem: string; status: number | null } }
>('documentosPadrao/verificarParaArbitragem', async (id, thunkAPI) => {
  try {
    return await documentosPadraoService.detalhar(id);
  } catch (erro) {
    return thunkAPI.rejectWithValue({
      mensagem: mensagemErro(erro, 'Este documento não está mais disponível.'),
      status: erro instanceof ErroHttp ? erro.status : null,
    });
  }
});

export const carregarContextoDocumentosPadrao = createAsyncThunk<
  DocumentosPadraoContexto,
  void,
  { rejectValue: string }
>('documentosPadrao/carregarContexto', async (_arg, thunkAPI) => {
  try {
    return await documentosPadraoService.carregarContexto();
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      mensagemErro(erro, 'Não foi possível carregar os setores do escritório.'),
    );
  }
});

/** Alimenta o seletor de parte do formulário, com busca server-side por nome ou CPF/CNPJ. */
export const buscarPartesDocumentosPadrao = createAsyncThunk<
  DocumentoPadraoParte[],
  string,
  { rejectValue: string }
>('documentosPadrao/buscarPartes', async (busca, thunkAPI) => {
  try {
    return await documentosPadraoService.buscarPartes(busca);
  } catch (erro) {
    return thunkAPI.rejectWithValue(mensagemErro(erro, 'Não foi possível buscar as partes.'));
  }
});

/** Versões e usos só existem no detalhe — a lista não os traz. */
export const carregarDetalheDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  string,
  { rejectValue: string }
>('documentosPadrao/detalhar', async (id, thunkAPI) => {
  try {
    return await documentosPadraoService.detalhar(id);
  } catch (erro) {
    return thunkAPI.rejectWithValue(mensagemErro(erro, 'Não foi possível carregar o documento.'));
  }
});

/** "Ver os outros N usos" deixou de expandir um array em memória e virou fetch paginado. */
export const carregarMaisUsosDocumentoPadrao = createAsyncThunk<
  DocumentoPadraoUso[],
  { id: string; page: number },
  { rejectValue: string }
>('documentosPadrao/carregarUsos', async ({ id, page }, thunkAPI) => {
  try {
    return await documentosPadraoService.listarUsos(id, page);
  } catch (erro) {
    return thunkAPI.rejectWithValue(mensagemErro(erro, 'Não foi possível carregar os usos.'));
  }
});

/**
 * Toda mutação recarrega a página corrente em vez de mexer no array local: com paginação e
 * contadores server-side, remendar o array faria os números da aba mentirem sobre a tela.
 */
async function mutarERecarregar<T>(
  operacao: () => Promise<T>,
  dispatch: (acao: ReturnType<typeof carregarDocumentosPadrao>) => unknown,
): Promise<T> {
  try {
    const resultado = await operacao();
    dispatch(carregarDocumentosPadrao());
    return resultado;
  } catch (erro) {
    // 404 (outra pessoa excluiu) e 409 (trava de uso, revisão velha) dizem que a tela está velha. O
    // reload é AGUARDADO: quando a rejeição chega à View, a linha já traz o `usosEmAndamento` novo,
    // e é ele que troca o diálogo de exclusão para a versão bloqueada.
    if (erro instanceof ErroHttp && STATUS_DE_LISTA_VELHA.includes(erro.status)) {
      await dispatch(carregarDocumentosPadrao());
    }
    throw erro;
  }
}

export const criarDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  DocumentoPadraoFormInput,
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/criar', async (input, thunkAPI) => {
  try {
    return await mutarERecarregar(() => documentosPadraoService.criar(input), thunkAPI.dispatch);
  } catch (erro) {
    return thunkAPI.rejectWithValue(paraErroMutacao(erro, 'Não foi possível salvar o documento.'));
  }
});

export const atualizarDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  { id: string; input: DocumentoPadraoFormInput; revisao: number },
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/atualizar', async ({ id, input, revisao }, thunkAPI) => {
  try {
    return await mutarERecarregar(
      () => documentosPadraoService.atualizar(id, input, revisao),
      thunkAPI.dispatch,
    );
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      paraErroMutacao(erro, 'Não foi possível salvar as alterações.'),
    );
  }
});

export const substituirArquivoDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  { id: string; arquivo: File },
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/substituirArquivo', async ({ id, arquivo }, thunkAPI) => {
  try {
    return await mutarERecarregar(
      () => documentosPadraoService.substituirArquivo(id, arquivo),
      thunkAPI.dispatch,
    );
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      paraErroMutacao(erro, 'Não foi possível substituir o arquivo.'),
    );
  }
});

export const restaurarVersaoDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  { id: string; versao: number },
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/restaurarVersao', async ({ id, versao }, thunkAPI) => {
  try {
    return await mutarERecarregar(
      () => documentosPadraoService.restaurarVersao(id, versao),
      thunkAPI.dispatch,
    );
  } catch (erro) {
    return thunkAPI.rejectWithValue(paraErroMutacao(erro, 'Não foi possível restaurar a versão.'));
  }
});

export const duplicarDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  string,
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/duplicar', async (id, thunkAPI) => {
  try {
    return await mutarERecarregar(() => documentosPadraoService.duplicar(id), thunkAPI.dispatch);
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      paraErroMutacao(erro, 'Não foi possível duplicar o documento.'),
    );
  }
});

export const arquivarDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  string,
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/arquivar', async (id, thunkAPI) => {
  try {
    return await mutarERecarregar(() => documentosPadraoService.arquivar(id), thunkAPI.dispatch);
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      paraErroMutacao(erro, 'Não foi possível arquivar o documento.'),
    );
  }
});

export const restaurarDocumentoPadrao = createAsyncThunk<
  DocumentoPadrao,
  string,
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/restaurar', async (id, thunkAPI) => {
  try {
    return await mutarERecarregar(() => documentosPadraoService.restaurar(id), thunkAPI.dispatch);
  } catch (erro) {
    return thunkAPI.rejectWithValue(
      paraErroMutacao(erro, 'Não foi possível restaurar o documento.'),
    );
  }
});

/**
 * A trava de exclusão real é o **409** do servidor — o mock excluía sem revalidar nada. Quem troca o
 * diálogo para a versão bloqueada é a lista recarregada por `mutarERecarregar`, não o corpo do erro.
 */
export const excluirDocumentoPadrao = createAsyncThunk<
  string,
  string,
  { rejectValue: ErroMutacaoDocumentoPadrao; state: RootState }
>('documentosPadrao/excluir', async (id, thunkAPI) => {
  try {
    return await mutarERecarregar(() => documentosPadraoService.excluir(id), thunkAPI.dispatch);
  } catch (erro) {
    return thunkAPI.rejectWithValue(paraErroMutacao(erro, 'Não foi possível excluir o documento.'));
  }
});
